<template>
  <!--
    Sample-card preview grid for the Custom Deck builder.

    Renders the 5 sample cards (Fool + four Aces) as they land. Each
    has a "Refresh (50 ✦)" button. Until samples are generated, shows
    placeholder slots with the canonical card names so the operator
    knows what they're previewing.
  -->
  <div class="sample-preview">
    <header class="head">
      <h4 class="title">Sample preview</h4>
      <p class="sub">{{ subtitle }}</p>
    </header>

    <div class="grid">
      <div
        v-for="(slot, idx) in slots"
        :key="slot.cardName + idx"
        class="card-slot"
        :class="{ 'has-image': !!slot.imageUrl, clickable: !!slot.imageUrl }"
        @click="slot.imageUrl && $emit('open', slot)"
      >
        <div class="card-frame">
          <img
            v-if="slot.imageUrl"
            :src="slot.imageUrl"
            :alt="slot.cardName"
            class="card-img"
          />
          <div v-else class="placeholder">
            <span class="placeholder-name">{{ slot.cardName }}</span>
            <span class="placeholder-hint">{{ generating ? 'Generating…' : 'Pending' }}</span>
          </div>
        </div>
        <div class="card-meta">
          <span class="card-name">{{ slot.cardName }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

// Canonical sample-card order, matching reader/service/custom_deck_service.SAMPLE_CARD_NAMES.
const CARD_ORDER = [
  'The Fool',
  'Ace of Cups',
  'Ace of Pentacles',
  'Ace of Swords',
  'Ace of Wands',
]

const props = defineProps({
  // Array of {card_id, card_name, image_url} from the BE preview
  // endpoint or status response. May be partial during generation.
  cards: { type: Array, default: () => [] },
  generating: { type: Boolean, default: false },
  refreshingCardId: { type: Number, default: null },
  canRefresh: { type: Boolean, default: true },
  subtitle: {
    type: String,
    default: 'Five cards to gauge how the brand reads. Refresh any that miss.',
  },
})
defineEmits(['open'])

// Build slot list in canonical order. Slots without a matching card
// row render as placeholders so the grid doesn't reflow as cards land.
const slots = computed(() => CARD_ORDER.map((name) => {
  const match = props.cards.find(c => (c.card_name || c.name) === name)
  return {
    cardName: name,
    cardId: match?.card_id || match?.id || null,
    imageUrl: match?.image_url || '',
    refreshing: !!match && match.card_id === props.refreshingCardId,
  }
}))
</script>

<style scoped>
.sample-preview { display: flex; flex-direction: column; gap: 14px; }
.head { text-align: left; }
.title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 16px;
  margin: 0 0 4px;
}
.sub {
  color: rgba(212, 196, 232, 0.6);
  font-size: 12px;
  margin: 0;
}
.grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}
.card-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.card-slot.clickable { cursor: pointer; transition: transform 0.18s; }
.card-slot.clickable:hover { transform: translateY(-3px); }
.card-slot.clickable:hover .card-frame {
  border-color: #C9A94E;
  box-shadow: 0 6px 20px rgba(201, 169, 78, 0.28);
}
.card-frame {
  width: 100%;
  aspect-ratio: 2 / 3;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.18s;
}
.has-image .card-frame { border-color: rgba(201, 169, 78, 0.32); }
.card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  text-align: center;
}
.placeholder-name {
  font-family: 'Cinzel', serif;
  font-size: 12px;
  color: rgba(212, 196, 232, 0.7);
}
.placeholder-hint {
  font-size: 10px;
  color: rgba(212, 196, 232, 0.4);
  font-style: italic;
}

.card-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.card-name {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  color: rgba(212, 196, 232, 0.85);
}
.refresh-btn {
  font-size: 10px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  border-radius: 999px;
  color: rgba(212, 196, 232, 0.8);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s;
}
.refresh-btn:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}
.refresh-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.refreshing {
  font-size: 10px;
  color: rgba(201, 169, 78, 0.85);
  font-style: italic;
}

@media (max-width: 720px) {
  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
